import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const verdictColor = {
  SALUDABLE: "#10b981",
  ESTABLE: "#3b82f6",
  EN_RIESGO: "#f59e0b",
  CRITICO: "#ef4444",
};

const badgeColor = {
  APPROVE: "#10b981",
  APPROVE_FREE: "#22d3ee",
  CONSIDER: "#f59e0b",
  REJECT: "#6b7280",
};

const modeColor = {
  NORMAL: "#10b981",
  SURVIVAL: "#f59e0b",
  CRITICAL: "#ef4444",
};

export function AuditorPanel() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");

  const fetchData = async () => {
    try {
      const [s, m] = await Promise.all([
        axios.get(`${API}/auditor/status`),
        axios.get(`${API}/survival/mode`).catch(() => ({ data: null })),
      ]);
      setData(s.data);
      setMode(m.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/auditor/run`, {}, { timeout: 25000 });
      await fetchData();
    } catch (e) {
      console.error(e);
    } finally {
      setRunning(false);
    }
  };

  const sendDigest = async () => {
    setDigesting(true);
    setDigestMsg("");
    try {
      const r = await axios.post(`${API}/auditor/digest/send?force=true`, {}, { timeout: 60000 });
      setDigestMsg(r.data?.status === "sent"
        ? `✓ Enviado · ${r.data.verdict}`
        : `✗ ${r.data?.status || "error"}`);
    } catch (e) {
      setDigestMsg("✗ fallo de envio");
    } finally {
      setDigesting(false);
    }
  };

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 60000);
    return () => clearInterval(i);
  }, []);

  if (loading) return <div style={{ padding: 16, color: "#94a3b8" }}>Cargando auditor...</div>;
  const latest = data?.latest || {};
  const forecasts = latest.forecasts || {};
  const capital = latest.capital || {};
  const sysIq = latest.system_iq || {};
  const exec = latest.executive_summary || {};
  const paid = latest.paid_api_justifications || [];
  const agents = latest.agent_audit || [];
  const correlations = latest.correlations || {};

  return (
    <div data-testid="auditor-panel" style={{ padding: 16, color: "#e2e8f0", fontSize: 13 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 8 }}>
            AUDITOR
            {mode?.mode && (
              <span data-testid="survival-mode-badge" style={{
                background: modeColor[mode.mode] || "#64748b",
                color: "white",
                padding: "2px 8px",
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
              }} title={(mode.reasons || []).join(" · ")}>
                {mode.mode} · ${mode.daily_budget_usd}/día
              </span>
            )}
            <span style={{ color: "#64748b", fontWeight: 400, fontSize: 11 }}>IBM SPSS · predikdata</span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            Snapshot #{data?.total_snapshots || 0} · {latest.timestamp ? new Date(latest.timestamp).toLocaleString() : "sin datos"}
            {mode?.disabled_phases?.length > 0 && (
              <span style={{ color: "#f59e0b", marginLeft: 6 }}>
                · fases OFF: {mode.disabled_phases.join(", ")}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            data-testid="auditor-digest-btn"
            onClick={sendDigest}
            disabled={digesting}
            style={{
              background: digesting ? "#334155" : "#8b5cf6",
              color: "white",
              border: "none",
              padding: "6px 10px",
              borderRadius: 6,
              cursor: digesting ? "default" : "pointer",
              fontSize: 11,
              fontWeight: 600,
            }}
            title="Envia el digest semanal por email ahora (forzado)"
          >
            {digesting ? "Enviando..." : "ENVIAR DIGEST"}
          </button>
          <button
            data-testid="auditor-run-btn"
            onClick={runAudit}
            disabled={running}
            style={{
              background: running ? "#334155" : "#0ea5e9",
              color: "white",
              border: "none",
              padding: "6px 12px",
              borderRadius: 6,
              cursor: running ? "default" : "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {running ? "Auditando..." : "EJECUTAR AUDIT"}
          </button>
        </div>
      </div>
      {digestMsg && (
        <div data-testid="auditor-digest-msg" style={{ fontSize: 10, color: digestMsg.startsWith("✓") ? "#10b981" : "#ef4444", marginTop: -6, marginBottom: 8 }}>
          {digestMsg}
        </div>
      )}

      {/* Verdict + System IQ */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        <Metric
          label="Veredicto"
          value={exec.veredicto_global || "N/A"}
          color={verdictColor[exec.veredicto_global] || "#64748b"}
          testid="auditor-verdict"
        />
        <Metric
          label="System IQ"
          value={`${sysIq.current ?? "—"}/100`}
          sub={`slope ${forecasts?.system_iq?.ols?.slope ?? 0}/día`}
          testid="auditor-system-iq"
        />
        <Metric
          label="Capital"
          value={`$${capital.remaining_usd ?? 0}`}
          sub={`${capital.days_of_capital_left ?? 0} días restantes`}
          color={capital.days_of_capital_left < 15 ? "#ef4444" : "#e2e8f0"}
          testid="auditor-capital"
        />
        <Metric
          label="Net 30d fcst"
          value={`$${forecasts.net_profit_30d_usd ?? 0}`}
          sub={`rev $${forecasts?.revenue_usd?.ols?.next_30d_usd ?? 0}`}
          color={(forecasts.net_profit_30d_usd ?? 0) < 0 ? "#ef4444" : "#10b981"}
          testid="auditor-net-forecast"
        />
      </div>

      {/* Bullets */}
      {exec.bullet_principales && (
        <div style={{ marginBottom: 12, padding: 10, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 6 }}>
          {exec.bullet_principales.map((b, i) => (
            <div key={i} style={{ fontSize: 12, color: "#cbd5e1", marginBottom: 3 }}>
              · {b}
            </div>
          ))}
          {exec.proxima_accion_humana && (
            <div style={{ marginTop: 6, padding: 6, background: "#1e293b", borderRadius: 4, fontSize: 11, color: "#fbbf24" }}>
              Próxima acción humana: <b>{exec.proxima_accion_humana}</b>
            </div>
          )}
        </div>
      )}

      {/* Paid APIs */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
          JUSTIFICACIÓN MATEMÁTICA · APIs PAGAS
        </div>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }} data-testid="auditor-paid-apis-table">
          <thead>
            <tr style={{ color: "#64748b" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Fuente</th>
              <th style={{ textAlign: "right", padding: 4 }}>$/mes</th>
              <th style={{ textAlign: "right", padding: 4 }}>Vars</th>
              <th style={{ textAlign: "right", padding: 4 }}>Uplift/mes</th>
              <th style={{ textAlign: "right", padding: 4 }}>Payback</th>
              <th style={{ textAlign: "right", padding: 4 }}>ROI 12m</th>
              <th style={{ textAlign: "center", padding: 4 }}>Veredicto</th>
            </tr>
          </thead>
          <tbody>
            {paid.map((j, i) => (
              <tr key={i} style={{ borderTop: "1px solid #1e293b" }}>
                <td style={{ padding: 4, color: "#e2e8f0" }}>{j.source}</td>
                <td style={{ padding: 4, textAlign: "right" }}>${j.monthly_cost_usd}</td>
                <td style={{ padding: 4, textAlign: "right" }}>{j.variables_unlocked}</td>
                <td style={{ padding: 4, textAlign: "right", color: "#10b981" }}>${j.expected_monthly_uplift_usd}</td>
                <td style={{ padding: 4, textAlign: "right" }}>
                  {j.payback_months >= 9999 ? "∞" : `${j.payback_months}mo`}
                </td>
                <td style={{ padding: 4, textAlign: "right" }}>
                  {j.roi_12m_pct >= 9999 ? "∞" : `${j.roi_12m_pct}%`}
                </td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <span style={{
                    background: badgeColor[j.verdict] || "#334155",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: 3,
                    fontSize: 10,
                    fontWeight: 600,
                  }}>
                    {j.verdict}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Agents weakest */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
          AGENTES CON MAYOR BRECHA (ordenados por cobertura ascendente)
        </div>
        <div data-testid="auditor-agent-audit" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {agents.slice(0, 6).map((a) => (
            <div key={a.agent} style={{ padding: 8, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#e2e8f0" }}>{a.agent}</div>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
                data {a.coverage_pct}% · predictivo {a.predictive_coverage_pct}% · faltan {a.variables_missing}
              </div>
              <div style={{ height: 4, background: "#1e293b", borderRadius: 2, marginTop: 4 }}>
                <div style={{
                  width: `${a.coverage_pct}%`,
                  height: "100%",
                  background: a.coverage_pct < 30 ? "#ef4444" : a.coverage_pct < 60 ? "#f59e0b" : "#10b981",
                  borderRadius: 2,
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Correlations */}
      {!correlations.insufficient_data && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
            CORRELACIONES PEARSON
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, fontSize: 11 }} data-testid="auditor-correlations">
            {["iq_vs_revenue", "iq_vs_views", "cost_vs_revenue", "views_vs_revenue"].map((k) => (
              <div key={k} style={{ padding: 6, background: "#0f172a", borderRadius: 4 }}>
                <div style={{ color: "#64748b", fontSize: 10 }}>{k}</div>
                <div style={{ color: "#e2e8f0", fontWeight: 600 }}>{correlations[k] ?? "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.pending_paid_api_alerts > 0 && (
        <div style={{ marginTop: 10, padding: 8, background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 4, fontSize: 11, color: "#fca5a5" }}>
          ⚠ {data.pending_paid_api_alerts} alerta(s) de API paga matemáticamente justificadas — revisa notificaciones humanas.
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, sub, color, testid }) {
  return (
    <div data-testid={testid} style={{ padding: 10, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 6 }}>
      <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || "#e2e8f0", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
